import { useEffect, useRef, useState } from "react";
import { loadCookieSdk } from "../services/cookieConsentService";
import type { CookieConsentResult } from "../types/cookieConsent";

interface UseCookieConsentOptions {
  policyUrl: string;
}

interface UseCookieConsentResult {
  consent: CookieConsentResult | null;
  isLoading: boolean;
  error: Error | null;
}

export function useCookieConsent(
  options: UseCookieConsentOptions,
): UseCookieConsentResult {
  const { policyUrl } = options;
  const [consent, setConsent] = useState<CookieConsentResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) {
      setIsLoading(false);
      return;
    }

    const init = async () => {
      try {
        setIsLoading(true);
        const result = await loadCookieSdk({ policyUrl });
        initRef.current = true;
        setConsent(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to initialise cookie consent"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [policyUrl]);

  return { consent, isLoading, error };
}
